/**
 * CategorySidebar components - Extracted from CategoryPage.jsx
 * Contains category filter and sidebar components
 */
import React from 'react';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * CategoryFilter - Category and subcategory navigation list
 */
export const CategoryFilter = ({
  categories,
  slug,
  selectedCategory,
  selectedSubcategory,
  expandedCategories,
  handleCategoryClick,
  toggleCategoryExpand
}) => (
  <div className="space-y-2 min-h-[300px]" data-testid="category-filter">
    <Label className="mb-2 block font-semibold">Categories</Label>
    
    {/* All Categories option */}
    <div
      onClick={() => handleCategoryClick('all')}
      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
        !selectedCategory && slug === 'all' 
          ? 'bg-blue-100 border-2 border-blue-500' 
          : 'hover:bg-gray-100 border-2 border-transparent'
      }`}
      data-testid="category-all"
    >
      <span className="font-medium">All Categories</span>
    </div>
    
    {/* Categories List - scrollable with responsive height */}
    <div className="max-h-[50vh] overflow-y-auto space-y-1 pr-1 scroll-smooth">
      {categories.map((category) => (
        <CategoryItem
          key={category.id}
          category={category}
          selectedCategory={selectedCategory}
          selectedSubcategory={selectedSubcategory}
          slug={slug}
          expandedCategories={expandedCategories}
          handleCategoryClick={handleCategoryClick}
          toggleCategoryExpand={toggleCategoryExpand}
        />
      ))}
    </div>
  </div>
);

/**
 * CategoryItem - Single category with optional subcategories
 */
const CategoryItem = ({
  category,
  selectedCategory,
  selectedSubcategory,
  slug,
  expandedCategories,
  handleCategoryClick,
  toggleCategoryExpand
}) => (
  <div>
    {/* Main Category */}
    <div
      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
        selectedCategory === category.slug && !selectedSubcategory
          ? 'bg-blue-100 border-2 border-blue-500' 
          : 'hover:bg-gray-100 border-2 border-transparent'
      }`}
      data-testid={`category-${category.slug}`}
    >
      <div 
        className="flex items-center gap-2 flex-1"
        onClick={() => handleCategoryClick(category.slug)}
      >
        <span className="text-lg">{category.icon}</span>
        <span className="text-sm font-medium">{category.name}</span>
      </div>
      <div className="flex items-center gap-1">
        <Badge variant="secondary" className="text-xs">
          {category.ad_count || 0}
        </Badge>
        {category.subcategories?.length > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleCategoryExpand(category.slug);
            }}
            className="p-1 hover:bg-gray-200 rounded"
            aria-label={expandedCategories[category.slug] ? 'Collapse' : 'Expand'}
          >
            {expandedCategories[category.slug] ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
    </div>
    
    {/* Subcategories */}
    {category.subcategories?.length > 0 && expandedCategories[category.slug] && (
      <div className="ml-6 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
        {category.subcategories.map((sub) => (
          <div
            key={sub.slug || sub.id}
            onClick={() => handleCategoryClick(sub.slug, true, category.slug)}
            className={`flex items-center justify-between p-2 rounded cursor-pointer transition-all ${
              selectedSubcategory === sub.slug || slug === sub.slug
                ? 'bg-blue-100 border-l-2 border-blue-500' 
                : 'hover:bg-gray-50'
            }`}
            data-testid={`subcategory-${sub.slug}`}
          >
            <span className="text-sm text-gray-700">{sub.name}</span>
            <Badge variant="outline" className="text-xs">
              {sub.ad_count || 0}
            </Badge>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default CategoryFilter;
